import type { EditorialMoneyPageCopy } from "@/components/site/EditorialMoneyPage";

export const HIRE_SHOPIFY_DEVELOPER_COPY = {
  en: {
    locale: "en",
    path: "/hire-shopify-developer",
    accent: "platform",
    eyebrow: "Shopify engineering capacity",
    title: "Hire a Shopify developer without creating a new bottleneck",
    intro:
      "Add senior Shopify engineering capacity for themes, Functions, checkout, apps, integrations, migrations, performance and technical SEO — with written scope, staging, release control and documented handoff.",
    answer:
      "If you already know you need implementation capacity, the decision is not simply freelancer versus agency. The important variables are ownership, continuity, review depth, platform breadth and what happens when the first change touches checkout, an app, an integration or SEO at the same time.",
    primaryLabel: "Request engineering capacity",
    secondaryLabel: "Talk through the work",
    trust: ["Shopify Partner since 2015", "Senior implementation", "Staging + release control"],
    diagram: {
      eyebrow: "Engagement model",
      title: "Capacity with an operating system around it",
      caption:
        "The developer is only one part of the delivery model. Scope, QA and continuity determine whether recurring Shopify work compounds or becomes technical debt.",
      columns: ["Layer", "Typical hire", "Shugert model"],
      rows: [
        ["Ownership", "Ticket-by-ticket execution", "Named owner, written scope and acceptance criteria"],
        ["Implementation", "Code against the immediate request", "Platform-aware implementation across theme, apps and Shopify surfaces"],
        ["Release", "Merge or deploy when code is done", "Staging, representative QA, rollback awareness and handoff"],
        ["Continuity", "Depends on one person's availability", "Documented context that can survive vacations, turnover and changing priorities"],
      ],
    },
    fit: {
      eyebrow: "When this fits",
      title: "Hire capacity when the work is real, recurring or interconnected",
      intro:
        "This page is for teams that have already crossed the educational stage and need someone to own implementation. If you are still deciding whether you need an agency at all, start with the development guide instead.",
      items: [
        {
          title: "Your backlog keeps growing",
          body:
            "Theme changes, app conflicts, merchandising requests and analytics fixes are waiting because the internal team cannot absorb Shopify work consistently.",
        },
        {
          title: "The change crosses systems",
          body:
            "A storefront request also touches an ERP, WMS, PIM, subscription app, customer account flow, Shopify Functions or checkout. That is where isolated implementation gets expensive.",
        },
        {
          title: "You need senior review",
          body:
            "The team can execute routine work but wants stronger architecture, code review, performance, SEO and release decisions around higher-risk changes.",
        },
        {
          title: "You are preparing a larger move",
          body:
            "A theme migration, replatform, Plus upgrade or B2B rollout is coming and you want continuity before, during and after launch rather than a one-off handoff.",
        },
      ],
    },
    thesis: {
      eyebrow: "What you are buying",
      title: "A Shopify developer should reduce coordination cost, not add to it",
      intro:
        "The cheapest implementation path can become the most expensive when knowledge fragments across freelancers, apps and undocumented fixes.",
      points: [
        {
          title: "One owner for the change",
          body:
            "Every job starts with the business constraint, affected Shopify surfaces, dependencies and acceptance criteria. That keeps a request from becoming a chain of unrelated patches.",
        },
        {
          title: "Senior decisions before code",
          body:
            "We distinguish native Shopify capability, theme work, Functions, app configuration, custom apps and external integrations before choosing the implementation path.",
        },
        {
          title: "Evidence at release",
          body:
            "A completed ticket is not the same as a verified outcome. We stage, test representative paths, record what changed and preserve enough context for the next release.",
        },
      ],
    },
    deliverables: {
      eyebrow: "What the engagement produces",
      title: "Visible outputs instead of invisible hours",
      intro:
        "The exact deliverables depend on the job, but the operating pattern stays consistent so the merchant knows what was changed and why.",
      columns: ["Output", "What it includes", "Why it matters"],
      rows: [
        ["Scope brief", "Affected surfaces, dependencies, assumptions and acceptance criteria", "Prevents a small request from silently expanding into an uncontrolled project"],
        ["Implementation", "Theme, Functions, app, API or integration changes appropriate to the problem", "Uses the smallest responsible technical surface"],
        ["QA evidence", "Representative storefront, checkout, account, device or data-flow checks", "Makes release confidence observable"],
        ["Handoff", "What changed, files/systems touched, limitations and next actions", "Preserves continuity for your team and future work"],
      ],
    },
    capabilities: {
      eyebrow: "Shopify coverage",
      title: "The work rarely stays inside one Liquid file",
      intro:
        "You can hire us for a narrow job, but the team can follow the dependency when that job touches another Shopify surface.",
      groups: [
        {
          title: "Storefront",
          items: ["Liquid and Online Store 2.0", "Theme architecture and sections", "JavaScript behavior", "Accessibility and responsive fixes"],
        },
        {
          title: "Platform",
          items: ["Shopify Functions", "Checkout extensibility", "Customer accounts", "Markets and localization"],
        },
        {
          title: "Apps & systems",
          items: ["Custom and embedded apps", "Admin/API integrations", "ERP, PIM, WMS and 3PL", "Subscriptions, reviews and marketing app conflicts"],
        },
        {
          title: "Commercial quality",
          items: ["Core Web Vitals", "Technical SEO", "Analytics instrumentation", "CRO implementation"],
        },
      ],
    },
    process: {
      eyebrow: "How work moves",
      title: "Scope → build → verify → handoff",
      intro:
        "The process is intentionally simple. The important part is that responsibility does not disappear between development and release.",
      steps: [
        { title: "Scope", body: "Define the business problem, affected surfaces, constraints and done criteria before implementation starts." },
        { title: "Build", body: "Implement in the smallest responsible Shopify surface and keep unrelated changes out of the release." },
        { title: "Verify", body: "Test representative customer and operational paths, not only whether the code compiles." },
        { title: "Handoff", body: "Document the change, limitations and next action so the next job begins with context rather than archaeology." },
      ],
    },
    evidence: {
      eyebrow: "Proof before promises",
      title: "Evaluate the team by the kind of Shopify work already shipped",
      body:
        "Shugert's public work includes large-catalog migrations, theme migrations with SEO recovery, custom storefront builds and long-running Shopify Plus relationships. The useful question is whether that evidence resembles the risk in your backlog.",
      points: [
        "Llantas Cavazos: 8,000+ SKU migration with search and inventory complexity",
        "Fashionable Canes: 15,000+ SKU theme migration and SEO recovery",
        "Petwell: 10,000+ SKU Magento to Shopify Plus migration",
        "Fashionable INC: multi-store Shopify Plus technical partnership",
      ],
      linkLabel: "Review Shopify work",
      linkHref: "/work",
      limitation:
        "Past project outcomes are context, not a guarantee that a new store will produce the same commercial result. Scope and acceptance criteria are defined for the current environment.",
    },
    boundaries: {
      eyebrow: "Developer vs freelancer vs agency",
      title: "Choose the model that matches the coordination risk",
      intro:
        "There is no universal best hiring model. A focused freelancer can be ideal for a bounded task. A managed engineering relationship becomes more useful as dependencies and continuity increase.",
      columns: [
        {
          title: "Freelancer can be enough",
          items: ["One bounded theme change", "Low operational risk", "Your team owns QA and release", "You already have architecture and documentation"],
        },
        {
          title: "Managed capacity fits better",
          items: ["Recurring Shopify backlog", "Multiple apps or systems", "SEO/performance consequences", "Need for continuity and senior review"],
        },
        {
          title: "Use a project instead",
          items: ["Replatforming", "Major theme rebuild", "B2B architecture", "ERP/PIM/WMS integration with a defined launch"],
        },
      ],
    },
    pricing: {
      eyebrow: "Commercial model",
      title: "Buy the smallest responsible amount of capacity",
      body:
        "Shugert uses fixed scopes for bounded work and recurring engineering capacity for continuous Shopify backlogs. The right model depends on store count, release risk, systems involved and how much context must be retained between jobs.",
      note:
        "For current engagement ranges, use the pricing page. This page owns the hiring decision, not agency pricing as a keyword target.",
    },
    related: {
      eyebrow: "Related decisions",
      title: "Use the right owner for the next question",
      intro: "These pages intentionally answer different jobs so the site does not make every page compete for the same query.",
      links: [
        { label: "Shopify Development Agency", href: "/shopify-experts", body: "Broad development-service intent: themes, apps, integrations and replatforming." },
        { label: "How to Choose a Shopify Expert", href: "/resources/how-to-choose-shopify-expert", body: "Evidence-based vetting criteria for an individual specialist." },
        { label: "Shopify Developer Cost in 2026", href: "/resources/shopify-developer-cost", body: "Market rates by hourly, project and retainer model, with sources." },
        { label: "Freelancer vs Agency Hourly Rates", href: "/resources/shopify-developer-hourly-rate", body: "What the rate difference pays for and when a freelancer is the better fit." },
        { label: "Pricing", href: "/pricing", body: "Current Shugert project and recurring engagement ranges." },
        { label: "Shopify Plus Agency", href: "/shopify-plus-agency", body: "Enterprise architecture, B2B, Functions, Markets, checkout and Plus work." },
      ],
    },
    faqs: [
      {
        q: "How much does it cost to hire a Shopify developer?",
        a:
          "It depends on whether the work is a bounded project or recurring capacity, the seniority required, and whether the change touches apps, integrations, checkout, SEO or release operations. Shugert publishes current engagement ranges on the pricing page instead of using a single hourly rate as a proxy for project cost.",
      },
      {
        q: "Should I hire a freelancer or a Shopify agency?",
        a:
          "For one isolated, low-risk task, a strong freelancer can be the right choice. Managed engineering becomes more useful when the backlog is recurring, multiple systems are involved, senior review is needed, or the team must preserve context across releases.",
      },
      {
        q: "Can I hire Shugert for ongoing Shopify development?",
        a:
          "Yes. Recurring capacity is designed for established stores with a steady Shopify backlog. Bounded migrations, rebuilds and integrations are usually scoped as projects instead.",
      },
      {
        q: "Do you work on Shopify Plus?",
        a:
          "Yes. Plus work can include Shopify Functions, checkout extensibility, B2B, Markets, multi-store architecture, custom apps and operational integrations. Enterprise Plus intent has its own service page.",
      },
      {
        q: "What happens after a developer finishes the change?",
        a:
          "The release should include verification and handoff. We document what changed, representative checks, known limitations and the next action so the merchant is not dependent on hidden implementation context.",
      },
    ],
    faqTitle: "Hiring a Shopify developer: common questions",
    finalTitle: "Add Shopify engineering capacity without losing ownership",
    finalBody:
      "Send the backlog, the constraint or the next release. We will identify the smallest responsible engagement and tell you when a narrower option is enough.",
    finalLabel: "Request engineering capacity",
    quoteIntent: "ongoing",
    quoteChallenge: "ongoing_help",
  },
  es: {
    locale: "es",
    path: "/es/contratar-desarrollador-shopify",
    accent: "platform",
    eyebrow: "Capacidad de ingeniería Shopify",
    title: "Contrata un desarrollador Shopify sin crear un nuevo cuello de botella",
    intro:
      "Incorpora capacidad senior para themes, Functions, checkout, apps, integraciones, migraciones, rendimiento y SEO técnico, con alcance escrito, staging, control de release y handoff documentado.",
    answer:
      "Si ya sabes que necesitas capacidad de implementación, la decisión no es sólo freelancer contra agencia. Importan el ownership, la continuidad, la profundidad de revisión, la cobertura de plataforma y qué ocurre cuando un cambio toca checkout, una app, una integración o SEO al mismo tiempo.",
    primaryLabel: "Solicitar capacidad de ingeniería",
    secondaryLabel: "Hablar del trabajo",
    trust: ["Shopify Partner desde 2015", "Implementación senior", "Staging + control de release"],
    diagram: {
      eyebrow: "Modelo de trabajo",
      title: "Capacidad con un sistema operativo alrededor",
      caption:
        "El desarrollador es sólo una parte del delivery. Alcance, QA y continuidad determinan si el trabajo recurrente acumula valor o deuda técnica.",
      columns: ["Capa", "Contratación típica", "Modelo Shugert"],
      rows: [
        ["Ownership", "Ejecución ticket por ticket", "Responsable, alcance escrito y criterios de aceptación"],
        ["Implementación", "Código contra la solicitud inmediata", "Implementación consciente de theme, apps y superficies Shopify"],
        ["Release", "Deploy cuando termina el código", "Staging, QA representativo, rollback y handoff"],
        ["Continuidad", "Depende de la disponibilidad de una persona", "Contexto documentado que sobrevive vacaciones, rotación y cambios de prioridad"],
      ],
    },
    fit: {
      eyebrow: "Cuándo encaja",
      title: "Contrata capacidad cuando el trabajo es real, recurrente o interconectado",
      intro:
        "Esta página es para equipos que ya pasaron la etapa educativa y necesitan ownership de implementación. Si aún estás decidiendo si necesitas agencia, empieza por la guía de desarrollo.",
      items: [
        { title: "El backlog sigue creciendo", body: "Cambios de theme, conflictos de apps, merchandising y analítica esperan porque el equipo interno no puede absorber Shopify de forma consistente." },
        { title: "El cambio cruza sistemas", body: "Una petición de storefront también toca ERP, WMS, PIM, subscriptions, customer accounts, Shopify Functions o checkout." },
        { title: "Necesitas revisión senior", body: "El equipo ejecuta trabajo rutinario pero necesita mejor arquitectura, code review, rendimiento, SEO y decisiones de release en cambios de mayor riesgo." },
        { title: "Preparas un movimiento mayor", body: "Viene una migración de theme, replatforming, upgrade a Plus o rollout B2B y quieres continuidad antes, durante y después del launch." },
      ],
    },
    thesis: {
      eyebrow: "Qué estás comprando",
      title: "Un desarrollador Shopify debe reducir el costo de coordinación",
      intro: "La implementación barata se vuelve cara cuando el conocimiento se fragmenta entre freelancers, apps y fixes sin documentación.",
      points: [
        { title: "Un owner por cambio", body: "Cada trabajo parte de la restricción de negocio, superficies afectadas, dependencias y criterios de aceptación." },
        { title: "Decisiones senior antes del código", body: "Separamos capacidad nativa, theme, Functions, configuración de apps, custom apps e integraciones antes de elegir la implementación." },
        { title: "Evidencia en el release", body: "Un ticket completado no equivale a resultado verificado. Hacemos staging, pruebas representativas y registramos lo que cambió." },
      ],
    },
    deliverables: {
      eyebrow: "Qué produce el engagement",
      title: "Outputs visibles en lugar de horas invisibles",
      intro: "Los entregables cambian por trabajo, pero el patrón operativo se mantiene para que el merchant sepa qué cambió y por qué.",
      columns: ["Output", "Incluye", "Por qué importa"],
      rows: [
        ["Scope brief", "Superficies, dependencias, supuestos y criterios de aceptación", "Evita que una petición pequeña se convierta en proyecto sin control"],
        ["Implementación", "Cambios de theme, Functions, apps, APIs o integración según el problema", "Usa la superficie técnica responsable más pequeña"],
        ["QA evidence", "Pruebas representativas de storefront, checkout, cuentas, dispositivos o flujos de datos", "Hace observable la confianza del release"],
        ["Handoff", "Qué cambió, archivos/sistemas tocados, límites y próximos pasos", "Preserva continuidad para el equipo y trabajo futuro"],
      ],
    },
    capabilities: {
      eyebrow: "Cobertura Shopify",
      title: "El trabajo rara vez se queda dentro de un archivo Liquid",
      intro: "Puedes contratarnos para un trabajo acotado, pero el equipo puede seguir la dependencia si toca otra superficie Shopify.",
      groups: [
        { title: "Storefront", items: ["Liquid y Online Store 2.0", "Arquitectura de themes y sections", "JavaScript", "Accesibilidad y responsive"] },
        { title: "Plataforma", items: ["Shopify Functions", "Checkout extensibility", "Customer accounts", "Markets y localización"] },
        { title: "Apps y sistemas", items: ["Custom y embedded apps", "Admin/API integrations", "ERP, PIM, WMS y 3PL", "Conflictos entre apps"] },
        { title: "Calidad comercial", items: ["Core Web Vitals", "SEO técnico", "Instrumentación analítica", "Implementación CRO"] },
      ],
    },
    process: {
      eyebrow: "Cómo se mueve el trabajo",
      title: "Scope → build → verify → handoff",
      intro: "El proceso es simple a propósito. Lo importante es que la responsabilidad no desaparezca entre desarrollo y release.",
      steps: [
        { title: "Scope", body: "Definir problema, superficies, restricciones y criterios de terminado antes de implementar." },
        { title: "Build", body: "Implementar en la superficie Shopify responsable más pequeña y dejar fuera cambios no relacionados." },
        { title: "Verify", body: "Probar recorridos representativos de cliente y operación, no sólo que el código compile." },
        { title: "Handoff", body: "Documentar cambio, límites y próxima acción para que el siguiente trabajo empiece con contexto." },
      ],
    },
    evidence: {
      eyebrow: "Prueba antes que promesas",
      title: "Evalúa al equipo por el tipo de trabajo Shopify ya entregado",
      body: "El trabajo público de Shugert incluye migraciones de catálogos grandes, migraciones de theme con recuperación SEO, custom storefronts y relaciones Shopify Plus de largo plazo.",
      points: [
        "Llantas Cavazos: migración de 8,000+ SKUs con complejidad de búsqueda e inventario",
        "Fashionable Canes: 15,000+ SKUs, migración de theme y recuperación SEO",
        "Petwell: 10,000+ SKUs de Magento a Shopify Plus",
        "Fashionable INC: partnership técnico Shopify Plus multi-store",
      ],
      linkLabel: "Ver trabajo Shopify",
      linkHref: "/es/proyectos",
      limitation: "Resultados anteriores son contexto, no garantía. El alcance y criterios se definen para el entorno actual.",
    },
    boundaries: {
      eyebrow: "Developer vs freelancer vs agencia",
      title: "Elige el modelo según el riesgo de coordinación",
      intro: "No existe un modelo universal. Un freelancer enfocado puede ser ideal para una tarea acotada; la ingeniería gestionada gana valor cuando aumentan dependencias y continuidad.",
      columns: [
        { title: "Freelancer puede bastar", items: ["Un cambio de theme", "Riesgo operativo bajo", "Tu equipo controla QA y release", "Ya tienes arquitectura y documentación"] },
        { title: "Capacidad gestionada encaja mejor", items: ["Backlog recurrente", "Varias apps o sistemas", "Consecuencias SEO/performance", "Continuidad y revisión senior"] },
        { title: "Usa un proyecto", items: ["Replatforming", "Rebuild mayor", "Arquitectura B2B", "Integración ERP/PIM/WMS con launch definido"] },
      ],
    },
    pricing: {
      eyebrow: "Modelo comercial",
      title: "Compra la cantidad responsable más pequeña de capacidad",
      body: "Shugert usa scopes fijos para trabajo acotado y capacidad recurrente para backlogs continuos. El modelo depende de tiendas, riesgo de release, sistemas involucrados y contexto que deba conservarse.",
      note: "Los rangos actuales están en la página de precios. Esta URL posee la decisión de contratación, no la keyword de precios de agencia.",
    },
    related: {
      eyebrow: "Decisiones relacionadas",
      title: "Usa el owner correcto para la siguiente pregunta",
      intro: "Estas páginas responden jobs distintos para no hacer que todo el sitio compita por la misma intención.",
      links: [
        { label: "Agencia de desarrollo Shopify", href: "/es/desarrollo-shopify", body: "Intención amplia de servicios de desarrollo." },
        { label: "Cómo elegir un experto Shopify", href: "/es/recursos/como-elegir-un-experto-shopify", body: "Criterios para evaluar a un especialista individual." },
        { label: "Precios", href: "/es/precios", body: "Rangos actuales de proyectos y capacidad recurrente." },
        { label: "Agencia Shopify Plus", href: "/es/agencia-shopify-plus", body: "Arquitectura enterprise, B2B, Functions, Markets y checkout." },
      ],
    },
    faqs: [
      { q: "¿Cuánto cuesta contratar un desarrollador Shopify?", a: "Depende de si el trabajo es proyecto o capacidad recurrente, seniority, riesgo de release y sistemas involucrados. Shugert publica rangos actuales en la página de precios en vez de usar una sola tarifa por hora como proxy." },
      { q: "¿Freelancer o agencia Shopify?", a: "Para una tarea aislada y de bajo riesgo, un buen freelancer puede ser suficiente. La capacidad gestionada aporta más cuando el backlog es recurrente, hay varios sistemas, hace falta revisión senior o debe conservarse contexto entre releases." },
      { q: "¿Puedo contratar a Shugert para desarrollo Shopify continuo?", a: "Sí. La capacidad recurrente está pensada para tiendas establecidas con backlog continuo. Migraciones, rebuilds e integraciones grandes suelen definirse como proyectos." },
      { q: "¿Trabajan con Shopify Plus?", a: "Sí. El trabajo Plus puede incluir Functions, checkout extensibility, B2B, Markets, multi-store, custom apps e integraciones. La intención enterprise tiene su propia página." },
      { q: "¿Qué pasa después de terminar el cambio?", a: "El release incluye verificación y handoff: qué cambió, checks representativos, límites conocidos y próxima acción para no depender de contexto oculto." },
    ],
    faqTitle: "Preguntas al contratar un desarrollador Shopify",
    finalTitle: "Añade capacidad Shopify sin perder ownership",
    finalBody: "Envía el backlog, la restricción o el próximo release. Identificamos el engagement responsable más pequeño y te decimos si una opción más acotada basta.",
    finalLabel: "Solicitar capacidad de ingeniería",
    quoteIntent: "ongoing",
    quoteChallenge: "ongoing_help",
  },
} satisfies Record<"en" | "es", EditorialMoneyPageCopy>;
